import gsap from "gsap";

/**
 * stickerWipeOn — animates elements like stickers being slapped onto the screen
 *
 * @param {string} selector  - CSS class selector, e.g. ".my-sticker"
 * @param {object} options   - optional config (see defaults below)
 *                           - useViewport (default: true) - fire only when entering viewport
 *                           - viewportMargin (default: "0px") - margin for intersection detection
 */
/**
 * fadeInUp — animates elements fading in and sliding up with staggered timing
 *
 * @param {string} selector  - CSS class selector, e.g. ".fade-in-up"
 * @param {object} options   - optional config (see defaults below)
 *                           - useViewport (default: true) - fire only when entering viewport
 *                           - viewportMargin (default: "0px") - margin for intersection detection
 */
export const fadeInUp = (selector, options = {}) => {
  const {
    duration       = 0.6,
    ease           = "power2.out",
    stagger        = 0.1,
    yStart         = -30,
    onComplete     = null,
    useViewport    = true,
    viewportMargin = "0px",
    delay          = 0,
  } = options;

  const elements = document.querySelectorAll(selector);
  if (!elements.length) return;

  // Hide elements initially so they don't flash before animation
  elements.forEach(el => {
    el.style.visibility = "hidden";
  });

  const playAnimation = (els) => {
    gsap.killTweensOf(els);

    // Set visibility for all elements
    els.forEach(el => {
      el.style.visibility = "visible";
    });

    gsap.fromTo(els,
      {
        opacity: 0,
        y:       yStart,
      },
      {
        opacity:   1,
        y:         0,
        duration,
        ease,
        stagger,
        delay,
        onComplete,
      }
    );
  };

  if (useViewport) {
    let pendingElements = [];
    let batchTimeout;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          pendingElements.push(entry.target);
          observer.unobserve(entry.target);
        }
      });

      // Batch elements entering within same animation frame
      clearTimeout(batchTimeout);
      batchTimeout = setTimeout(() => {
        if (pendingElements.length) {
          playAnimation(pendingElements);
          pendingElements = [];
        }
      }, 16); // ~1 frame at 60fps
    }, { rootMargin: viewportMargin });

    elements.forEach(el => observer.observe(el));
  } else {
    playAnimation(elements);
  }
};

export const stickerWipeOn = (selector, options = {}) => {
  const {
    duration       = 0.45,
    ease           = "back.out(1.8)",
    stagger        = 0.12,
    rotationRange  = 12,
    scaleStart     = 0.4,
    onComplete     = null,
    useViewport    = true,
    viewportMargin = "-20% 0px -20% 0px",
    // viewportMargin = '0px',
    delay          = 0.5,
  } = options;

  const elements = document.querySelectorAll(selector);
  if (!elements.length) return;

  // Hide elements initially so they don't flash before animation
  elements.forEach(el => {
    el.style.visibility = "hidden";
  });

  const playAnimation = (els) => {
    gsap.killTweensOf(els);

    // Set visibility and compute a rotation for each element up front
    els.forEach(el => {
      const dir = Math.random() > 0.5 ? 1 : -1;
      const rotation = (Math.random() * rotationRange + rotationRange * 0.5) * dir;
      el.style.visibility = "visible";
      el.dataset.gsapRotation = rotation;
    });

    gsap.fromTo(els,
      {
        opacity:         0,
        scale:           scaleStart,
        rotation:        (_, el) => el.dataset.gsapRotation * 2.5,
        y:               -28,
      //   clipPath:        "inset(0% 100% 0% 0%)",
        transformOrigin: "50% 50%",
      },
      {
        opacity:   1,
        scale:     1,
        rotation:  (_, el) => Number(el.dataset.gsapRotation),
        y:         0,
      //   clipPath:  "inset(0% 0% 0% 0%)",
        duration,
        ease,
        stagger,
        onComplete,
        // delay,
      }
    );
  };

  if (useViewport) {
    let pendingElements = [];
    let batchTimeout;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          pendingElements.push(entry.target);
          observer.unobserve(entry.target);
        }
      });

      // Batch elements entering within same animation frame
      clearTimeout(batchTimeout);
      batchTimeout = setTimeout(() => {
        if (pendingElements.length) {
          playAnimation(pendingElements);
          pendingElements = [];
        }
      }, 16); // ~1 frame at 60fps
    }, { rootMargin: viewportMargin });

    elements.forEach(el => observer.observe(el));
  } else {
    playAnimation(elements);
  }
};